/* eslint eqeqeq: "off" */
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import DirectionsRunIcon from "@material-ui/icons/DirectionsRun";
import PanToolIcon from "@material-ui/icons/PanTool";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button as BootstrapButton,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

const useStyles = makeStyles(() => ({
  defaultColumn: { width: "25%", textAlign: "left" },
  statusColumn: { width: "2.5%", textAlign: "center" },
  iconColumn: { width: "25%", textAlign: "center" },
  attendanceStatus: { width: "17.5%", textAlign: "center" },
  identifierColumn: { width: "5%", textAlign: "center" },
  badgeSuccess: {
    backgroundColor: "#54afe2",
    color: "white",
    borderRadius: "5px",
    padding: "5px",
    fontSize: "12px",
  },
  badgeDanger: {
    backgroundColor: "#dc3545",
    color: "white",
    borderRadius: "5px",
    padding: "5px",
    fontSize: "12px",
  },
  badgeWarning: {
    backgroundColor: "#ffc107",
    color: "white",
    borderRadius: "5px",
    padding: "5px",
    fontSize: "12px",
  },
}));

/**
 * The program's session weekdays, from school_program.day ("Monday" or
 * "Monday,Wednesday"). Unknown names are dropped.
 */
export const parseProgramDays = (day) =>
  String(day || "")
    .split(",")
    .map((d) => d.trim())
    .filter((d) => moment.weekdays().includes(d));

/**
 * The session the sheet opens on: today when today is a program day,
 * otherwise the most recent program day (up to a week back). A program with
 * no recognisable day falls back to today.
 */
export const defaultSessionDate = (today, days) => {
  const start = moment(today).startOf("day");
  if (!days.length) return start.format("YYYY-MM-DD");
  for (let back = 0; back < 7; back++) {
    const candidate = start.clone().subtract(back, "days");
    if (days.includes(candidate.format("dddd"))) {
      return candidate.format("YYYY-MM-DD");
    }
  }
  return start.format("YYYY-MM-DD");
};

const statusId = (options, label) => {
  const found = options.find((o) => o.label === label);
  return found ? found.value : null;
};

/**
 * Attendance sheet for one school program on one date. Reads and saves through
 * GET/PUT api/school-programs/:id/attendance/:date; statuses come from the
 * shared attendance lookup (same list the Batches dialog uses). The date is
 * free to pick, since a school may shift a session to another weekday; a
 * date that is not one of the program's days is flagged but still allowed.
 */
export function SchoolProgramAttendanceDialog({
  programId,
  days,
  show,
  onHide,
  onSaved,
}) {
  const classes = useStyles();
  const programDays = parseProgramDays(days);
  const [date, setDate] = useState("");
  const [students, setStudents] = useState([]);
  const [comment, setComment] = useState("");
  const [weekId, setWeekId] = useState(null);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);
  const [optionsFailed, setOptionsFailed] = useState(false);
  // Every sheet load takes a ticket; a response whose ticket is no longer the
  // latest (date changed, dialog closed and reopened) is dropped, so a slow
  // reply can never overwrite a newer sheet.
  const loadTicket = useRef(0);

  const presentId = statusId(options, "Present");
  const absentId = statusId(options, "Absent");
  const unmarkedId = statusId(options, "Unmarked");

  const loadSheet = async (sessionDate) => {
    if (!programId || !sessionDate) return;
    const ticket = ++loadTicket.current;
    setIsLoading(true);
    setError("");
    setErrorDetails([]);
    try {
      const response = await axios.get(
        `api/school-programs/${programId}/attendance/${sessionDate}`
      );
      if (ticket !== loadTicket.current) return;
      const data = response.data.data || {};
      setStudents(data.student_list || []);
      setComment(data.comment || "");
      setWeekId(data.attendance_week_id || null);
    } catch (e) {
      if (ticket !== loadTicket.current) return;
      setStudents([]);
      setComment("");
      setWeekId(null);
      const body = e.response && e.response.data;
      setError((body && body.message) || "Could not load the attendance sheet.");
    } finally {
      if (ticket === loadTicket.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !programId) {
      // Closed: drop any in-flight reply and clear the sheet so the next open
      // never paints the previous program's students.
      loadTicket.current++;
      setStudents([]);
      setComment("");
      setWeekId(null);
      setError("");
      setErrorDetails([]);
      return;
    }
    const initial = defaultSessionDate(new Date(), programDays);
    setDate(initial);
    loadSheet(initial);
    setOptionsFailed(false);
    axios
      .get("api/batches/attendance/options")
      .then((response) => {
        const list = response.data.results || [];
        setOptions(list);
        setOptionsFailed(!list.some((o) => o.label === "Unmarked"));
      })
      .catch(() => {
        setOptions([]);
        setOptionsFailed(true);
      });
    // eslint-disable-next-line
  }, [show, programId]);

  const setMark = (index, id) => {
    setStudents((list) =>
      list.map((s, i) =>
        i === index
          ? {
              ...s,
              // Clicking the active status again clears it back to Unmarked.
              attendance_id: s.attendance_id === id ? unmarkedId : id,
            }
          : s
      )
    );
  };

  // Saving needs the Unmarked status to fill in untouched students; without
  // the options list every one of them would go up as null and be rejected.
  const canSave =
    !isSaving && !isLoading && students.length > 0 && !!date && unmarkedId != null;

  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError("");
    setErrorDetails([]);
    try {
      await axios.put(`api/school-programs/${programId}/attendance/${date}`, {
        // As typed (trimmed): the server stores it as a parameter, so
        // doubling apostrophes here would save "Sam''s".
        comment: comment.trim(),
        student_list: students.map((s) => ({
          user_id: s.user_id,
          attendance_id: s.attendance_id || unmarkedId,
        })),
      });
      setIsSaving(false);
      onSaved && onSaved();
      onHide();
    } catch (e) {
      const body = e.response && e.response.data;
      setError(
        (body && body.message) || "Could not save attendance. Please try again."
      );
      setErrorDetails(
        Array.isArray(body && body.errors)
          ? body.errors.map((d) => `${d.user_id != null ? `#${d.user_id}: ` : ""}${d.reason}`)
          : []
      );
      setIsSaving(false);
    }
  };

  const nameOf = (id) => {
    const found = students.find((s) => s.user_id === id);
    return found ? `${found.first_name} ${found.last_name}` : `student ${id}`;
  };

  const offDay =
    date && programDays.length > 0 && !programDays.includes(moment(date).format("dddd"));

  const badgeFor = (id) =>
    id === presentId
      ? classes.badgeSuccess
      : id === absentId
      ? classes.badgeDanger
      : classes.badgeWarning;
  const labelFor = (id) => {
    const found = options.find((o) => o.value === id);
    return found ? found.label : "-";
  };

  return (
    <Modal
      show={show}
      onHide={isSaving ? () => {} : onHide}
      backdrop={isSaving ? "static" : true}
      keyboard={!isSaving}
      aria-labelledby="school-program-attendance"
      size="lg"
    >
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title id="school-program-attendance">School Program Attendance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <fieldset disabled={isLoading || isSaving}>
          <div className="form-group row">
            <div className="col-lg-4">
              <input
                type="date"
                name="attendance_date"
                aria-label="Attendance date"
                className="form-control"
                value={date}
                onChange={(e) => {
                  const next = e.target.value;
                  setDate(next);
                  loadSheet(next);
                }}
              />
              <span className="form-text text-muted ml-1">
                Attendance Date
                {programDays.length > 0 && ` (program runs ${programDays.join(", ")})`}
              </span>
              {offDay && (
                <span className="form-text text-warning ml-1">
                  {moment(date).format("dddd")} is not a scheduled program day.
                </span>
              )}
            </div>
          </div>
          {error && (
            <div className="alert alert-danger py-2">
              {error}
              {errorDetails.length > 0 && (
                <ul className="mb-0 mt-1 pl-4">
                  {errorDetails.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.statusColumn}></TableCell>
                <TableCell className={classes.identifierColumn}>ID</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell className={classes.attendanceStatus}>Attendance</TableCell>
                <TableCell className={classes.iconColumn}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && students.length > 0 && options.length > 0 ? (
                students.map((student, index) => {
                  const marked =
                    student.attendance_id != null && student.attendance_id !== unmarkedId;
                  return (
                    <TableRow
                      key={student.user_id}
                      style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa" }}
                    >
                      <TableCell className={classes.statusColumn}>
                        <OverlayTrigger
                          overlay={
                            <Tooltip id={`sp-att-${student.user_id}`}>
                              {marked ? "Marked Attendance" : "Unmarked Attendance"}
                            </Tooltip>
                          }
                        >
                          {marked ? (
                            <i className="fa fa-check-circle text-success fa-lg" />
                          ) : (
                            <i className="fa fa-info-circle" style={{ color: "#54afe2" }} />
                          )}
                        </OverlayTrigger>
                      </TableCell>
                      <TableCell className={classes.identifierColumn}>{student.user_id}</TableCell>
                      <TableCell className={classes.defaultColumn}>{student.first_name}</TableCell>
                      <TableCell className={classes.defaultColumn}>{student.last_name}</TableCell>
                      <TableCell className={classes.attendanceStatus}>
                        {student.attendance_id != null ? (
                          <Badge pill className={badgeFor(student.attendance_id)}>
                            {labelFor(student.attendance_id)}
                          </Badge>
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                      <TableCell className={classes.iconColumn}>
                        <OverlayTrigger
                          overlay={<Tooltip id={`sp-present-${student.user_id}`}>Student Present</Tooltip>}
                        >
                          <BootstrapButton
                            variant={student.attendance_id === presentId ? "primary" : "secondary"}
                            className="mr-2 btn-sm"
                            aria-label={`Mark ${nameOf(student.user_id)} present`}
                            aria-pressed={student.attendance_id === presentId}
                            onClick={() => setMark(index, presentId)}
                          >
                            <PanToolIcon fontSize="small" />
                          </BootstrapButton>
                        </OverlayTrigger>
                        <OverlayTrigger
                          overlay={<Tooltip id={`sp-absent-${student.user_id}`}>Student Absent</Tooltip>}
                        >
                          <BootstrapButton
                            variant={student.attendance_id === absentId ? "danger" : "secondary"}
                            className="mr-2 btn-sm"
                            aria-label={`Mark ${nameOf(student.user_id)} absent`}
                            aria-pressed={student.attendance_id === absentId}
                            onClick={() => setMark(index, absentId)}
                          >
                            <DirectionsRunIcon fontSize="small" />
                          </BootstrapButton>
                        </OverlayTrigger>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ fontSize: "1.25rem" }}>
                    {isLoading ? (
                      <LinearProgress color="secondary" />
                    ) : optionsFailed ? (
                      "Could not load the attendance statuses. Close and try again."
                    ) : (
                      "No Students"
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="form-group row" style={{ marginTop: "3rem" }}>
            <div className="col-lg-12">
              <label htmlFor="sp-attendance-comment">Attendance Comment</label>
              <textarea
                id="sp-attendance-comment"
                name="comment"
                className="form-control"
                placeholder="Comment"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              {weekId && (
                <span className="form-text text-muted">
                  Attendance for this date has been saved before; saving again updates it.
                </span>
              )}
            </div>
          </div>
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        <BootstrapButton variant="secondary" onClick={onHide} disabled={isSaving}>
          Close
        </BootstrapButton>
        <BootstrapButton variant="primary" onClick={save} disabled={!canSave}>
          {isSaving ? "Saving..." : "Save"}
        </BootstrapButton>
      </Modal.Footer>
    </Modal>
  );
}
